import React, { useContext } from "react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
  Grid,
  IconButton,
  Icons,
  Loader,
  Modal,
  Text,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import { useAppointmentQuery } from "../../../apis/appointmentApi.hooks";
import { getCompleteName } from "../../../utils/misc";
import AppointmentStatusChip from "../AppointmentStatusChip/AppointmentStatusChip";
import styles from "./AppointmentInfoModal.module.scss";

interface AppointmentInfoModalProps {
  appointmentUuid?: string;
  visible: boolean;
  onClose: VoidFunction;
}

function AppointmentInfoModal(props: AppointmentInfoModalProps) {
  const { visible, onClose } = props;

  return (
    <Modal width="400px" visible={visible} closeModal={onClose}>
      <AppointmentInfoModalContent {...props} />
    </Modal>
  );
}

function AppointmentInfoModalContent(props: AppointmentInfoModalProps) {
  const { appointmentUuid, onClose } = props;

  const app = useContext(AppContext);

  const { data } = useAppointmentQuery(appointmentUuid!, {
    enabled: !!appointmentUuid,
  });

  if (!data) {
    return (
      <Grid container>
        <Grid item xs={12} container justifyContent="center" paddingY={4}>
          <Loader />
        </Grid>
      </Grid>
    );
  }

  const client = getCompleteName(data?.user?.firstName, data?.user?.lastName);
  const staff = getCompleteName(data?.staff?.firstName, data?.staff?.lastName);
  const date = format(new Date(data.startTime), "EEEE, LLLL d");
  const start = formatInTimeZone(
    new Date(data.startTime),
    app.client!.timezone,
    "hh:mmaaa",
  );
  const end = formatInTimeZone(
    new Date(data.endTime),
    app.client!.timezone,
    "hh:mmaaa",
  );
  const time = `${start} - ${end}`;
  const canceledOn = data.cancelledAt
    ? formatInTimeZone(
        new Date(data.cancelledAt),
        app.client!.timezone,
        "LLLL do, yyyy hh:mmaaa",
      )
    : undefined;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.close}>
          <IconButton icon={Icons.Close} size={13} onClick={onClose} />
        </div>
        <div className={styles.title}>
          <Text variant="heading-5" weight="600">
            {data?.appointmentType?.name}
          </Text>
        </div>
        <div className={styles.date}>
          <Text variant="body-lg" color="wolf-600" component="span">
            {date}
          </Text>
          <Text variant="body-lg" color="wolf-600" component="span">
            {time}
          </Text>
        </div>
      </div>
      <div className={styles.body}>
        <dl className={styles.list}>
          <div>
            <dt className={styles.term}>
              <Text variant="body-lg" color="wolf-600">
                Status
              </Text>
            </dt>
            <dd className={styles.description}>
              <AppointmentStatusChip data={data} />
            </dd>
          </div>
          <div>
            <dt className={styles.term}>
              <Text variant="body-lg" color="wolf-600">
                Client
              </Text>
            </dt>
            <dd className={styles.description}>
              <Text variant="body-lg" color="wolf-800">
                {client}
              </Text>
            </dd>
          </div>
          <div>
            <dt className={styles.term}>
              <Text variant="body-lg" color="wolf-600">
                Staff
              </Text>
            </dt>
            <dd className={styles.description}>
              <Text variant="body-lg" color="wolf-800">
                {staff}
              </Text>
            </dd>
          </div>
          {canceledOn && (
            <div>
              <dt className={styles.term}>
                <Text variant="body-lg" color="wolf-600">
                  Cancelled on
                </Text>
              </dt>
              <dd className={styles.description}>
                <Text variant="body-lg" color="wolf-800">
                  {canceledOn}
                </Text>
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}

export default AppointmentInfoModal;
